import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Box,
  TextField,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const BluetoothConnect = ({ open, onClose, onDeviceSelect }) => {
  const [devices, setDevices] = useState([]);
  const [recentDevices, setRecentDevices] = useState([]);
  const [deviceNames, setDeviceNames] = useState({});
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameDeviceId, setRenameDeviceId] = useState('');
  const [renameDeviceName, setRenameDeviceName] = useState('');

  useEffect(() => {
    const storedDevices =
      JSON.parse(localStorage.getItem('recentDevices')) || [];
    setRecentDevices(storedDevices);

    const storedNames = JSON.parse(localStorage.getItem('deviceNames')) || {};
    setDeviceNames(storedNames);
  }, []);

  const scanForDevices = async () => {
    setScanning(true);
    setError(null);
    setDevices([]);

    try {
      // Debug: attempt LE scan (works only if site has experimental flag enabled)
      if (navigator.bluetooth.requestLEScan) {
        console.log('Attempting LE scan...');
        const scan = await navigator.bluetooth.requestLEScan({
          acceptAllAdvertisements: true,
        });

        navigator.bluetooth.addEventListener(
          'advertisementreceived',
          (event) => {
            console.log('Advertisement received:', {
              name: event.device.name,
              id: event.device.id,
              rssi: event.rssi,
              txPower: event.txPower,
              uuids: event.uuids,
            });
          }
        );

        setTimeout(() => {
          scan.stop();
          console.log('LE scan stopped');
        }, 10000);
      }

      // Fallback to requestDevice (opens picker)
      console.log('Opening device picker...');
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true, // loosen filters for debugging
        optionalServices: ['0000ffe0-0000-1000-8000-00805f9b34fb'],
      });

      if (device) {
        console.log('Device selected:', device);
        setDevices([{ name: device.name || 'Unknown', id: device.id, device }]);
      }
    } catch (err) {
      console.error('Bluetooth scan failed:', err);
      setError('Bluetooth scan failed or was canceled.');
    }

    setScanning(false);
  };

  const handleDeviceSelect = async (selectedDevice) => {
    try {
      let device = selectedDevice.device; // Get the device if it's available

      // If selecting from "Recently Connected", re-request the device from Bluetooth API
      if (!device) {
        device = await navigator.bluetooth.requestDevice({
          filters: [{ name: selectedDevice.name }],
          optionalServices: ['0000ffe0-0000-1000-8000-00805f9b34fb'],
        });
      }

      await device.gatt.connect();
      onDeviceSelect(device); // Pass the full BluetoothDevice object

      const updatedRecentDevices = [
        { name: device.name, id: device.id },
        ...recentDevices.filter((d) => d.id !== device.id),
      ].slice(0, 5); // Keep only last 5

      setRecentDevices(updatedRecentDevices);
      localStorage.setItem(
        'recentDevices',
        JSON.stringify(updatedRecentDevices)
      );

      onClose();
    } catch (err) {
      setError('Failed to connect to device.');
    }
  };

  const removeRecentDevice = (deviceId) => {
    const updatedDevices = recentDevices.filter(
      (device) => device.id !== deviceId
    );
    setRecentDevices(updatedDevices);
    localStorage.setItem('recentDevices', JSON.stringify(updatedDevices));

    const updatedNames = { ...deviceNames };
    delete updatedNames[deviceId];
    setDeviceNames(updatedNames);
    localStorage.setItem('deviceNames', JSON.stringify(updatedNames));
  };

  const removeAvailableDevice = (deviceId) => {
    setDevices(devices.filter((device) => device.id !== deviceId));
  };

  const openRenameDialog = (deviceId, currentName) => {
    setRenameDeviceId(deviceId);
    setRenameDeviceName(currentName);
    setRenameDialogOpen(true);
  };

  const handleRenameDevice = () => {
    const updatedNames = { ...deviceNames, [renameDeviceId]: renameDeviceName };
    setDeviceNames(updatedNames);
    localStorage.setItem('deviceNames', JSON.stringify(updatedNames));

    setRenameDialogOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{ maxWidth: '100vw', margin: '0 auto', overflowX: 'hidden' }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 1,
          width: '100%',
        }}
      >
        <DialogTitle
          sx={{
            flexGrow: 1,
            fontSize: '18px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: 0,
          }}
        >
          Connect to Bluetooth Device
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ color: 'gray', padding: '5px' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Dialog Content - Prevents Unnecessary Scrollbar */}
      <DialogContent
        sx={{
          maxHeight: 'auto',
          overflowY: devices.length > 5 ? 'auto' : 'hidden', // Only scrolls when needed
          overflowX: 'hidden',
          paddingBottom: 2,
          width: '100%',
        }}
      >
        {/* Scan Button */}
        <Button
          variant="contained"
          startIcon={<BluetoothIcon />}
          onClick={scanForDevices}
          disabled={scanning}
          fullWidth
          sx={{
            mb: 2,
            fontWeight: 'bold',
            fontSize: '14px',
            textTransform: 'none',
          }}
        >
          {scanning ? 'Scanning...' : 'Scan for Devices'}
        </Button>

        {scanning && (
          <CircularProgress
            size={24}
            sx={{ display: 'block', margin: '16px auto' }}
          />
        )}
        {error && (
          <Typography color="error" sx={{ textAlign: 'center', mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Recently Connected Devices */}
        {recentDevices.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{ textAlign: 'center', mb: 1, fontSize: '14px' }}
            >
              Recently Connected
            </Typography>
            <List>
              {recentDevices.map((device) => (
                <ListItem
                  key={device.id}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    px: 1, // reduce horizontal padding
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    onClick={() => handleDeviceSelect(device)}
                    sx={{ flexGrow: 1, cursor: 'pointer' }}
                  >
                    <ListItemText
                      primary={deviceNames[device.id] || device.name}
                      secondary={
                        <>
                          {device.version && (
                            <Typography variant="body2" color="textSecondary">
                              Version: {device.version}
                            </Typography>
                          )}
                          <Typography variant="body2" color="textSecondary">
                            {device.id}
                          </Typography>
                        </>
                      }
                    />
                  </Box>
                  <IconButton
                    onClick={() =>
                      openRenameDialog(
                        device.id,
                        deviceNames[device.id] || device.name
                      )
                    }
                    sx={{ color: 'blue' }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => removeRecentDevice(device.id)}
                    sx={{ color: 'red' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Available Devices */}
        <Typography
          variant="h6"
          sx={{ textAlign: 'center', mb: 1, fontSize: '14px' }}
        >
          Available Devices
        </Typography>
        <List>
          {devices.length === 0 && !scanning ? (
            <Typography
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                color: 'gray',
                fontSize: '14px',
              }}
            >
              No devices found. Try scanning again.
            </Typography>
          ) : (
            devices.map((device) => (
              <ListItem
                key={device.id}
                sx={{
                  backgroundColor: '#f0f0f0',
                  borderRadius: 1,
                  mb: 1,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box
                  onClick={() => handleDeviceSelect(device)}
                  sx={{ flexGrow: 1, cursor: 'pointer' }}
                >
                  <ListItemText
                    primary={deviceNames[device.id] || device.name}
                    secondary={device.id}
                  />
                </Box>
                <IconButton
                  onClick={() =>
                    openRenameDialog(
                      device.id,
                      deviceNames[device.id] || device.name
                    )
                  }
                  sx={{ color: 'blue' }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => removeAvailableDevice(device.id)}
                  sx={{ color: 'red' }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>

      {/* Rename Device Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 'bold' }}>
          Rename Device
        </DialogTitle>

        <DialogContent
          sx={{ padding: '20px 24px 0px 24px', overflow: 'hidden' }}
        >
          <TextField
            fullWidth
            label="New Device Name"
            variant="outlined"
            value={renameDeviceName}
            onChange={(e) => setRenameDeviceName(e.target.value)}
            sx={{
              marginTop: 2,
              '& .MuiInputBase-root': { height: '45px' },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ paddingBottom: 2, paddingRight: 2 }}>
          <Button onClick={() => setRenameDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleRenameDevice}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BluetoothConnect;
